import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

/**
 * Hộp xác nhận trong trang (thay hộp thoại mặc định của trình duyệt), đồng bộ giao diện Hub.
 * - ask({...})              → true / false
 * - ask({..., input: {...}}) → chuỗi người dùng nhập, hoặc null nếu bấm Hủy
 */
interface Opts {
  title: string;
  message?: React.ReactNode;
  confirm?: string;          // chữ nút xác nhận
  cancel?: string;
  danger?: boolean;          // nút đỏ cho thao tác không hoàn tác được
  input?: { label: string; placeholder?: string; value?: string; required?: boolean; type?: string; readOnly?: boolean };
  hideCancel?: boolean;
}

function Dialog({ o, done }: { o: Opts; done: (v: string | boolean | null) => void }) {
  const [v, setV] = useState(o.input?.value ?? "");
  const [err, setErr] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    (field.current ?? box.current?.querySelector<HTMLButtonElement>("button.primary, button.danger"))?.focus();
    if (o.input?.readOnly) field.current?.select();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") done(o.input ? null : false); };
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); prev?.focus?.(); };
  }, [done, o.input]);
  const ok = () => {
    if (o.input?.required && !v.trim()) { setErr(true); field.current?.focus(); return; }
    done(o.input ? v.trim() : true);
  };
  return (
    <div className="dlgBack" onMouseDown={(e) => e.target === e.currentTarget && done(o.input ? null : false)}>
      <div className="dlg" role="alertdialog" aria-modal="true" aria-labelledby="dlgT" ref={box}>
        <h2 id="dlgT">{o.title}</h2>
        {o.message && <div className="dlgMsg">{o.message}</div>}
        {o.input && (
          <div className="field" style={{ flex: "none" }}>
            <label htmlFor="dlgIn">{o.input.label}</label>
            <input id="dlgIn" ref={field} type={o.input.type ?? "text"} value={v} readOnly={o.input.readOnly} placeholder={o.input.placeholder}
              onChange={(e) => { setV(e.target.value); setErr(false); }} onKeyDown={(e) => e.key === "Enter" && ok()} />
            {err && <span className="dlgErr">Vui lòng nhập {o.input.label.toLowerCase()}.</span>}
          </div>
        )}
        <div className="dlgBtns">
          {!o.hideCancel && <button className="btn" onClick={() => done(o.input ? null : false)}>{o.cancel ?? "Hủy"}</button>}
          <button className={`btn ${o.danger ? "danger" : "primary"}`} onClick={ok}>{o.confirm ?? "Đồng ý"}</button>
        </div>
      </div>
    </div>
  );
}

function open<T>(o: Opts): Promise<T> {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (v: string | boolean | null) => { root.unmount(); host.remove(); resolve(v as T); };
    root.render(<Dialog o={o} done={done} />);
  });
}

export const ask = (o: Omit<Opts, "input">) => open<boolean>(o);
export const askText = (o: Opts & { input: NonNullable<Opts["input"]> }) => open<string | null>(o);
